import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';

export default function BundleListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canPerformAction } = usePermission();
  const canEdit = canPerformAction('묶음 등록/편집');
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('mine'); // 'mine' | 'all'

  const load = useCallback(async () => {
    setLoading(true);
    try { setBundles(await api.get('/bundles')); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = bundles
    .filter(b => tab === 'all' || String(b.created_by) === String(user?.id))
    .filter(b => !q || b.name.toLowerCase().includes(q.toLowerCase()) || (b.description||'').toLowerCase().includes(q.toLowerCase()));

  const toggleVisibility = async (b, e) => {
    e.stopPropagation();
    const next = b.visibility === 'public' ? 'private' : 'public';
    try {
      await api.patch(`/bundles/${b.id}/visibility`, { visibility: next });
      setBundles(prev => prev.map(x => x.id === b.id ? { ...x, visibility: next } : x));
    } catch (err) { alert(err.message); }
  };

  const cloneBundle = async (b, e) => {
    e.stopPropagation();
    try {
      const created = await api.post(`/bundles/${b.id}/clone`);
      load();
      navigate(`/bundles/${created.id}`);
    } catch (err) { alert(err.message); }
  };

  const removeBundle = async (b, e) => {
    e.stopPropagation();
    if (!window.confirm(`"${b.name}" 묶음을 삭제하시겠습니까?`)) return;
    try {
      await api.del(`/bundles/${b.id}`);
      setBundles(prev => prev.filter(x => x.id !== b.id));
    } catch (err) { alert(err.message); }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom:16, padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button className={`btn btn-sm ${tab==='mine'?'btn-primary':'btn-outline'}`} onClick={()=>setTab('mine')}>내 묶음</button>
          <button className={`btn btn-sm ${tab==='all'?'btn-primary':'btn-outline'}`} onClick={()=>setTab('all')}>전체</button>
          <input className="form-input" style={{ marginLeft:8, width:240, padding:'6px 10px' }}
            placeholder="이름·설명 검색" value={q} onChange={e=>setQ(e.target.value)} />
        </div>
        {canEdit && <button className="btn btn-primary btn-sm" onClick={()=>navigate('/bundles/new')}>+ 새 묶음 만들기</button>}
      </div>

      {loading ? (
        <div className="card" style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>로딩 중...</div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:60, color:'#94a3b8' }}>
          {tab === 'mine'
            ? '내가 만든 묶음이 없습니다. [+ 새 묶음 만들기]로 만들거나, 전체에서 가져오기로 다른 묶음을 복제할 수 있습니다.'
            : '묶음이 없습니다. [+ 새 묶음 만들기]로 만들어 보세요.'}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap:14 }}>
          {filtered.map(b => {
            const isMine = String(b.created_by) === String(user?.id);
            const isPublic = b.visibility === 'public';
            return (
              <div key={b.id} className="card" style={{ padding:18, cursor:'pointer', position:'relative' }} onClick={()=>navigate(`/bundles/${b.id}`)}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span className={`badge ${isPublic ? 'badge-green' : 'badge-gray'}`}>
                    {isPublic ? '🌐 공개' : '🔒 비공개'}
                  </span>
                  <span style={{ fontSize:11, color:'#94a3b8' }}>{(b.updated_at||'').slice(0,10)}</span>
                </div>
                <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>{b.name}</div>
                {b.description && <div style={{ fontSize:12, color:'#64748b', marginBottom:10, lineHeight:1.5, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>{b.description}</div>}
                <div style={{ display:'flex', gap:12, fontSize:11, color:'#64748b', marginBottom:10 }}>
                  <span>항목 {b.item_count || 0}개</span>
                  <span>작성 {b.created_by_name}</span>
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={(e)=>{e.stopPropagation(); navigate(`/product-estimate/new?bundle=${b.id}`);}}>📝 견적하기</button>
                  {isMine ? (
                    <>
                      {canEdit && <button className="btn btn-outline btn-sm" onClick={(e)=>{e.stopPropagation(); navigate(`/bundles/${b.id}/edit`);}}>✎ 편집</button>}
                      {canEdit && <button className="btn btn-outline btn-sm" onClick={(e)=>toggleVisibility(b, e)}>
                        {isPublic ? '🔒 비공개로' : '🌐 공개로'}
                      </button>}
                      {canEdit && <button className="btn btn-outline btn-sm" onClick={(e)=>cloneBundle(b, e)}>🔁 복제</button>}
                      {canPerformAction('묶음 삭제') && <button className="btn btn-outline btn-sm" style={{ color:'#ef4444', borderColor:'#fecaca' }} onClick={(e)=>removeBundle(b, e)}>🗑 삭제</button>}
                    </>
                  ) : (
                    canEdit && <button className="btn btn-outline btn-sm" onClick={(e)=>cloneBundle(b, e)}>📥 내 카탈로그로 가져오기</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
